import { configureStore, getDefaultMiddleware } from "@reduxjs/toolkit"
import { intlReducer } from "react-intl-redux"
import { reducer as formReducer } from "redux-form"
import { combineReducers } from "redux"
import { createBrowserHistory } from "history"
import {
  initialState as confirmDialogInitialState,
  reducer as confirmDialogReducer
} from "./component/dialog"
import {
  actions as notifier,
  reducer as notifierReducer
} from "./component/notifier"
import users from "./api/userSlice"
import auth from "./api/authSlice"
import widget from "./api/widgetSlice"
import esign from "./api/esignSlice"
import userForm from "./api/userFormSlice"
import documents from "./pages/document/documentSlice"
import funds from "./pages/assetmanagement/fundsSlice"

import ui from "./commons/uiSlice"
import formSlice from "./api/formSlice"
import localeFr from "./i18n/fr.json"
import license from "./pages/admin/licensing/licenseSlice"
import accountSmtp from "./api/smtpAccountSlice"
import resources from "./api/resourceSlice"
import template from "./api/templateMailSlice"
import templateReducer from "./api/templateReducer"

/**
 * App initial state built from all "Slice"
 */
export const initialState = {
  intl: {
    defaultLocale: "fr",
    locale: "fr",
    messages: localeFr
  },
  am: funds.initialState,
  auth: auth.initialState,
  user: users.initialState,
  esign: esign.initialState,
  widgets: widget.initialState,
  documents: documents.initialState,
  userForm: userForm.initialState,
  formSlice: formSlice.initialState,
  ui: ui.initialState,
  confirmDialog: confirmDialogInitialState,
  license: license.initialState,
  smtpAccount: accountSmtp.initialState,
  resource: resources.initialState,
  template: template.initialState,
  templateReducer: templateReducer.initialState
}

/**
 * App Redux main reducer built from all "Slice"
 */
export const rootReducer = {
  // General API
  user: users.reducer,
  auth: auth.reducer,
  widgets: widget.reducer,
  am: funds.reducer,
  esign: esign.reducer,
  documents: documents.reducer,
  userForm: userForm.reducer,
  ui: ui.reducer,
  confirmDialog: confirmDialogReducer,
  notifications: notifierReducer,
  // For libs (intl, toastr, redux form)
  intl: intlReducer,
  form: formReducer,
  formSlice: formSlice.reducer,
  license: license.reducer,
  smtpAccount: accountSmtp.reducer,
  resource: resources.reducer,
  template: template.reducer,
  templateReducer: templateReducer.reducer
}

// disable it to see non serializable value error
const middlewareOpts = {
  serializableCheck: false
}

//
// those props are disabled when packaging a production build
const devToolsOpts = {
  latency: 0,
  shouldCatchErrors: true,
  immutableCheck: false
}

/**
 * A global try/catch middleware that dispatch a notification error.
 *
 * Error is commonly created by global Axios http error interceptor, to get more details see below components.
 * @see src/api/httpClientErrorHandler
 * @see src/api/httpClient
 *
 * @param store
 * @returns {function(*): function(*=): (Promise<*>|null|undefined)}
 */
const catchReduxError = (store) => (next) => (action) => {
  // eslint-disable-next-line consistent-return
  return Promise.resolve(next(action)).catch((err) => {
    const history = createBrowserHistory({ forceRefresh: true })
    const currentPath = history.location.pathname

    if (err.type && err.type === 401 && currentPath !== "/login") {
      return history.replace("/login")
    }

    // dispatch a notification by default
    store.dispatch(notifier.enqueueError(err.message))
  })
}

/**
 * A configure store that enhanced default API of store instance. This is used to split properly our webapp.
 *
 * @see https://redux.js.org/recipes/code-splitting
 * @param initialStateArg
 * @returns {EnhancedStore<any, AnyAction>}
 */
export const configureAppStore = (initialStateArg) => {
  const appStore = configureStore({
    reducer: rootReducer,
    middleware: [catchReduxError, ...getDefaultMiddleware(middlewareOpts)],
    devTools: devToolsOpts,
    preloadedState: initialStateArg
  })

  // Add a dictionary to keep track of the registered async reducers
  appStore.asyncReducers = {}

  // Create an inject reducer function
  // This function adds the async reducer, and creates a new combined reducer
  appStore.injectReducer = (key, asyncReducer) => {
    const newReducers = combineReducers({
      ...rootReducer,
      ...appStore.asyncReducers,
      [key]: asyncReducer
    })
    appStore.asyncReducers = { ...appStore.asyncReducers, [key]: asyncReducer }
    appStore.replaceReducer(newReducers)
  }

  // Return the modified store
  return appStore
}

export default configureAppStore(initialState)
